import React, { useState, useEffect, useCallback } from 'react';
import { Table, Select, Input, Popconfirm, Spin, Button } from 'antd';
import type { TableColumnsType } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { type AppDispatch } from '../../../../redux/store';
import {
  getAgentsAction,
  deleteAgentAction,
} from '../../store/interestSchemeActions';
import { type Agent } from '../../store/interestSchemeTypes';
import OutlinedButton from '../../../../components/ButtonComponent/OutlinedButton';
import './index.scss';

const PAGE_SIZE = 20;

export default function AgentTable(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();

  const [branchOptions, setBranchOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const stored = localStorage.getItem('branches');
    if (stored != null) {
      const parsed: Array<{ code: string; id: string }> = JSON.parse(stored);
      setBranchOptions(parsed.map((b) => ({ label: b.code, value: b.id })));
    }
  }, []);

  const fetchAgents = useCallback(
    async (page: number, branch: string, search: string) => {
      setIsLoading(true);
      const result = await getAgentsAction(dispatch, {
        branch: branch !== '' ? branch : undefined,
        search: search !== '' ? search : undefined,
        page,
        limit: PAGE_SIZE,
      });
      if (result !== null) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setAgents(result.agents);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setTotal(result.total);
      }
      setIsLoading(false);
    },
    [dispatch]
  );

  const handleSearch = () => {
    setCurrentPage(1);
    void fetchAgents(1, selectedBranch, searchText);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    void fetchAgents(page, selectedBranch, searchText);
  };

  const handleDelete = async (id: string) => {
    const success = await deleteAgentAction(dispatch, id);
    if (success) {
      void fetchAgents(currentPage, selectedBranch, searchText);
    }
  };

  const columns: TableColumnsType<Agent> = [
    {
      title: 'Customer ID',
      dataIndex: 'customerId',
      key: 'customerId',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Branches',
      key: 'branches',
      render: (_, record) => {
        const branches = record.branches ?? [];
        if (branches.length === 0) return '-';
        return branches
          .map(b => (typeof b === 'object' && b !== null ? (b as { code: string }).code : String(b)))
          .join(', ');
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        if (record.isAttached) {
          return <span style={{ color: '#94A3B8', fontSize: 12 }}>Linked to account</span>;
        }
        return (
          <Popconfirm
            title="Delete this agent?"
            description="This action cannot be undone."
            onConfirm={() => { void handleDelete(String(record._id)); }}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button icon={<DeleteOutlined />} size="small" shape="circle" danger />
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <div className="agent__table__page">
      <h1>Agents</h1>

      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="customers__input__container">
          <h4>Branch</h4>
          <Select
            style={{ width: 200, height: 56 }}
            placeholder="All branches"
            options={branchOptions}
            allowClear
            onChange={(value: string | undefined) => { setSelectedBranch(value ?? ''); }}
            className="deposit__select"
          />
        </div>
        <div className="customers__input__container">
          <h4>Search</h4>
          <Input
            style={{ width: 220, height: 56, borderRadius: 12 }}
            placeholder="Agent code or name"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); }}
            onPressEnter={handleSearch}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <OutlinedButton label="Search" onClick={handleSearch} />
        </div>
      </div>

      <Spin spinning={isLoading}>
        <Table
          dataSource={agents}
          columns={columns}
          rowKey="_id"
          bordered
          pagination={{
            current: currentPage,
            pageSize: PAGE_SIZE,
            total,
            onChange: handlePageChange,
            showSizeChanger: false,
            showTotal: (t) => `${t} agents`,
          }}
          locale={{ emptyText: 'No agents found. Use the search controls above or create one from the Customer form (type: Agent).' }}
        />
      </Spin>
    </div>
  );
}
